import { type CRRFlashQuestion } from './crr-flash-questions';

export type JetskiFlashQuestion = Omit<CRRFlashQuestion, 'category'> & {
  category: 'reglementation' | 'securite' | 'technique' | 'navigation' | 'environnement';
};

export const jetskiFlashQuestions: JetskiFlashQuestion[] = [
  // ── Réglementation ──
  {
    id: "jet-permis",
    question_fr: "Quel permis faut-il pour piloter un jet ski ?",
    question_en: "What license is needed to ride a jet ski?",
    answer_fr: "Le permis côtier (option côtière du permis plaisance)",
    answer_en: "The coastal license (coastal option of the leisure license)",
    context_fr: "Obligatoire dès 6 CV. Pas de permis spécifique « jet ski ».",
    context_en: "Mandatory from 6 HP. No specific 'jet ski' license.",
    category: "reglementation"
  },
  {
    id: "jet-age",
    question_fr: "À partir de quel âge peut-on conduire un jet ski ?",
    question_en: "What is the minimum age to ride a jet ski?",
    answer_fr: "16 ans (avec le permis côtier)",
    answer_en: "16 years old (with coastal license)",
    context_fr: "Les mineurs de moins de 16 ans peuvent être passagers.",
    context_en: "Minors under 16 can be passengers.",
    category: "reglementation"
  },
  {
    id: "jet-zone300",
    question_fr: "Un jet ski peut-il naviguer dans la bande des 300 mètres ?",
    question_en: "Can a jet ski navigate within the 300-meter zone?",
    answer_fr: "NON, sauf pour l'accès perpendiculaire au rivage à vitesse réduite",
    answer_en: "NO, except for perpendicular shore access at reduced speed",
    context_fr: "La bande des 300m protège les baigneurs — jet ski interdit sauf chenaux.",
    context_en: "The 300m zone protects swimmers — jet ski prohibited except in channels.",
    category: "reglementation"
  },
  {
    id: "jet-immat",
    question_fr: "L'immatriculation est-elle obligatoire pour un jet ski ?",
    question_en: "Is registration mandatory for a jet ski?",
    answer_fr: "Oui, carte de circulation obligatoire pour tout VNM (véhicule nautique à moteur)",
    answer_en: "Yes, registration card mandatory for all PWC (personal watercraft)",
    context_fr: "Le numéro doit être visible sur la coque.",
    context_en: "The number must be visible on the hull.",
    category: "reglementation"
  },
  // ── Sécurité ──
  {
    id: "jet-coupe-circuit",
    question_fr: "À quoi sert le coupe-circuit sur un jet ski ?",
    question_en: "What is the kill switch on a jet ski for?",
    answer_fr: "Arrêter le moteur automatiquement si le pilote tombe à l'eau",
    answer_en: "Automatically stop the engine if the rider falls off",
    context_fr: "Le cordon doit TOUJOURS être attaché au poignet ou au gilet.",
    context_en: "The lanyard must ALWAYS be attached to wrist or vest.",
    category: "securite"
  },
  {
    id: "jet-gilet",
    question_fr: "Quel type de gilet faut-il en jet ski ?",
    question_en: "What type of vest is needed for jet ski?",
    answer_fr: "Gilet de sauvetage ou aide à la flottabilité (50N minimum)",
    answer_en: "Life jacket or buoyancy aid (50N minimum)",
    context_fr: "Un gilet d'impact (type wakeboard) ne suffit PAS réglementairement.",
    context_en: "An impact vest (wakeboard type) is NOT sufficient legally.",
    category: "securite"
  },
  {
    id: "jet-distance",
    question_fr: "Quelle distance minimale entre deux jet skis en navigation ?",
    question_en: "What minimum distance between two jet skis while riding?",
    answer_fr: "Pas de distance légale fixe, mais distance de sécurité obligatoire",
    answer_en: "No fixed legal distance, but safety distance mandatory",
    context_fr: "En pratique, gardez au moins 50 mètres pour réagir.",
    context_en: "In practice, keep at least 50 meters to react.",
    category: "securite"
  },
  {
    id: "jet-nuit",
    question_fr: "Peut-on naviguer en jet ski la nuit ?",
    question_en: "Can you ride a jet ski at night?",
    answer_fr: "NON — la navigation de nuit est interdite pour les VNM",
    answer_en: "NO — night navigation is prohibited for PWC",
    context_fr: "Navigation autorisée uniquement du lever au coucher du soleil.",
    context_en: "Navigation allowed only from sunrise to sunset.",
    category: "securite"
  },
  // ── Technique ──
  {
    id: "jet-propulsion",
    question_fr: "Comment fonctionne la propulsion d'un jet ski ?",
    question_en: "How does a jet ski's propulsion work?",
    answer_fr: "Par hydrojet — aspiration d'eau et éjection à haute pression",
    answer_en: "By waterjet — water intake and high-pressure ejection",
    context_fr: "Pas d'hélice exposée, ce qui réduit le risque de blessure.",
    context_en: "No exposed propeller, reducing injury risk.",
    category: "technique"
  },
  {
    id: "jet-direction",
    question_fr: "Un jet ski peut-il tourner sans accélérer ?",
    question_en: "Can a jet ski turn without throttle?",
    answer_fr: "NON — sans propulsion, le jet ski ne répond plus à la direction",
    answer_en: "NO — without propulsion, the jet ski doesn't respond to steering",
    context_fr: "C'est la différence majeure avec un bateau classique : pas de gouvernail passif.",
    context_en: "Major difference with a regular boat: no passive rudder.",
    category: "technique"
  },
  // ── Navigation ──
  {
    id: "jet-chenal",
    question_fr: "Comment un jet ski doit-il traverser la bande des 300m ?",
    question_en: "How must a jet ski cross the 300m zone?",
    answer_fr: "Perpendiculairement à la côte, à vitesse réduite (< 5 nœuds)",
    answer_en: "Perpendicular to shore, at reduced speed (< 5 knots)",
    context_fr: "Utilisez les chenaux balisés quand ils existent.",
    context_en: "Use marked channels when available.",
    category: "navigation"
  },
  {
    id: "jet-distance-nav",
    question_fr: "Jusqu'à combien de milles peut-on naviguer en jet ski ?",
    question_en: "How far can you navigate on a jet ski?",
    answer_fr: "Jusqu'à 6 milles d'un abri (avec permis côtier et armement adapté)",
    answer_en: "Up to 6 miles from shelter (with coastal license and proper equipment)",
    context_fr: "En pratique, l'autonomie en carburant limite souvent la distance.",
    context_en: "In practice, fuel range often limits the distance.",
    category: "navigation"
  },
  // ── Environnement ──
  {
    id: "jet-natura2000",
    question_fr: "Peut-on faire du jet ski dans une zone Natura 2000 ?",
    question_en: "Can you jet ski in a Natura 2000 zone?",
    answer_fr: "Cela dépend — certaines zones ont des restrictions ou interdictions spécifiques",
    answer_en: "It depends — some zones have specific restrictions or prohibitions",
    context_fr: "Consultez la réglementation locale et les arrêtés préfectoraux.",
    context_en: "Check local regulations and prefectural decrees.",
    category: "environnement"
  },
  {
    id: "jet-bruit",
    question_fr: "Y a-t-il des limites de bruit pour les jet skis ?",
    question_en: "Are there noise limits for jet skis?",
    answer_fr: "Oui — les VNM doivent respecter les normes d'émission sonore (75 dB max)",
    answer_en: "Yes — PWC must comply with noise emission standards (75 dB max)",
    context_fr: "Les modèles récents sont de plus en plus silencieux.",
    context_en: "Recent models are increasingly quiet.",
    category: "environnement"
  },
];
